import React, {useEffect, useMemo, useState} from "react";
import {
    createUserWithEmailAndPassword,
    sendEmailVerification,
    signOut,
} from "firebase/auth";
import {Link, useNavigate} from "react-router-dom";
import logo from "../assets/tiply_logo_v5.png";
import {auth} from "../firebase";
import {useAuth} from "../context/AuthProvider";
import {
    isSelfServiceSignupEnabled,
    normalizeBusinessID,
    PRIVACY_VERSION,
    SUPPORTED_ONBOARDING_COUNTRIES,
    TERMS_VERSION,
} from "../lib/onboarding";
import {
    checkBusinessIdAvailability,
    completeBusinessSignup,
} from "../services/onboardingService";

const emptyBusiness = {
    address: "",
    businessID: "",
    countryCode: "EE",
    displayName: "",
    legalName: "",
    repFirstName: "",
    repLastName: "",
    repPhone: "",
};

const authErrorMessage = (error) => {
    if (error?.code === "auth/email-already-in-use") return "An account already exists for that email. Log in to continue.";
    if (error?.code === "auth/invalid-email") return "Enter a valid email address.";
    if (error?.code === "auth/weak-password") return "Choose a password with at least 8 characters.";
    if (error?.code === "auth/unauthorized-continue-uri") return "Email verification is not configured for this website yet. Please contact team@tiply.ee.";
    if (error?.code === "auth/too-many-requests") return "Too many attempts were made. Wait a moment, then try again.";
    return error?.message || "We could not create the account. Please try again.";
};

const Step = ({number, label, active, complete}) => (
    <div className={`flex items-center gap-2 ${active || complete ? "text-[#0d7c5f]" : "text-gray-400"}`}>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${active || complete ? "bg-[#e8f5f0]" : "bg-gray-100"}`}>
            {complete ? "✓" : number}
        </span>
        <span className="hidden text-xs font-semibold sm:block">{label}</span>
    </div>
);

export default function BusinessSignupPage() {
    const navigate = useNavigate();
    const {currentUser, rawUserData, userData, loading: authLoading} = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [business, setBusiness] = useState(emptyBusiness);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [availability, setAvailability] = useState(null);
    const [verificationRefresh, setVerificationRefresh] = useState(0);
    const signupEnabled = isSelfServiceSignupEnabled();

    const emailVerified = currentUser?.emailVerified === true;
    const stage = currentUser ? (emailVerified ? 3 : 2) : 1;

    useEffect(() => {
        if (authLoading || !rawUserData) return;
        if (rawUserData.role === "ADMIN") navigate("/admin", {replace: true});
        else if (userData?.businessID) navigate("/business/dashboard", {replace: true});
        else navigate("/select-venue", {replace: true});
    }, [authLoading, navigate, rawUserData, userData]);

    useEffect(() => {
        if (!currentUser || currentUser.emailVerified) return undefined;
        const timer = window.setInterval(async () => {
            try {
                await currentUser.reload();
                if (currentUser.emailVerified) {
                    await currentUser.getIdToken(true);
                    setVerificationRefresh((value) => value + 1);
                }
            } catch (pollError) {
                console.warn("Unable to refresh email verification status:", pollError);
            }
        }, 5000);
        return () => window.clearInterval(timer);
    }, [currentUser, verificationRefresh]);

    useEffect(() => {
        if (stage !== 3 || !business.businessID || business.businessID.length < 3) {
            setAvailability(null);
            return undefined;
        }
        let cancelled = false;
        setAvailability({checking: true});
        const timer = window.setTimeout(async () => {
            try {
                const result = await checkBusinessIdAvailability(business.businessID);
                if (!cancelled) setAvailability(result);
            } catch (availabilityError) {
                if (!cancelled) setAvailability({available: false, message: availabilityError.message});
            }
        }, 450);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [business.businessID, stage]);

    const progress = useMemo(() => ({account: stage > 1, verification: stage > 2}), [stage]);

    const createAccount = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
            await sendEmailVerification(credential.user, {
                url: `${window.location.origin}/signup`,
            });
            setNotice(`We sent a verification link to ${credential.user.email}.`);
        } catch (accountError) {
            setError(authErrorMessage(accountError));
        } finally {
            setBusy(false);
        }
    };

    const refreshVerification = async () => {
        setBusy(true);
        setError("");
        try {
            await currentUser.reload();
            await currentUser.getIdToken(true);
            if (!currentUser.emailVerified) setNotice("The email is not verified yet. Open the link in your inbox, then try again.");
            setVerificationRefresh((value) => value + 1);
        } catch (refreshError) {
            setError(refreshError.message);
        } finally {
            setBusy(false);
        }
    };

    const resendVerification = async () => {
        setBusy(true);
        setError("");
        try {
            await sendEmailVerification(currentUser, {url: `${window.location.origin}/signup`});
            setNotice(`A new verification link was sent to ${currentUser.email}.`);
        } catch (resendError) {
            setError(authErrorMessage(resendError));
        } finally {
            setBusy(false);
        }
    };

    const updateBusiness = (event) => {
        const {name, value} = event.target;
        setBusiness((current) => {
            const next = {...current, [name]: value};
            if (name === "displayName" && (!current.businessID || current.businessID === normalizeBusinessID(current.displayName))) {
                next.businessID = normalizeBusinessID(value);
            }
            if (name === "businessID") next.businessID = normalizeBusinessID(value);
            return next;
        });
    };

    const finishSignup = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError("");
        try {
            await currentUser.reload();
            await currentUser.getIdToken(true);
            if (!currentUser.emailVerified) throw new Error("Verify your email before continuing.");
            if (!termsAccepted) throw new Error("Accept the Terms of Use and Privacy Policy to continue.");
            const payload = {
                ...business,
                businessID: normalizeBusinessID(business.businessID),
                privacyVersion: PRIVACY_VERSION,
                termsVersion: TERMS_VERSION,
            };
            await completeBusinessSignup(payload);
            window.location.assign("/business/dashboard");
        } catch (signupError) {
            setError(signupError.message);
        } finally {
            setBusy(false);
        }
    };

    if (authLoading) {
        return <div className="min-h-screen bg-[#f6f6f9] flex items-center justify-center"><div className="h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-[#0d7c5f]" /></div>;
    }

    if (!signupEnabled && !currentUser) {
        return (
            <div className="min-h-screen bg-[#f6f6f9] px-4 py-12 flex items-center justify-center">
                <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
                    <img src={logo} alt="Tiply" className="mx-auto w-32" />
                    <h1 className="mt-8 text-2xl font-bold text-gray-900">Self-service signup is opening soon</h1>
                    <p className="mt-3 text-gray-600">We can still onboard your business personally.</p>
                    <a href="mailto:team@tiply.ee" className="mt-6 inline-flex rounded-xl bg-[#0d7c5f] px-5 py-3 font-semibold text-white">Contact Tiply</a>
                    <Link to="/login" className="mt-5 block text-sm font-medium text-[#0d7c5f]">Already have an account? Log in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f6f6f9] px-4 py-8 sm:py-12">
            <div className="mx-auto w-full max-w-2xl">
                <Link to="/"><img src={logo} alt="Tiply" className="mx-auto w-32" /></Link>
                <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-9">
                    <div className="mb-8 flex items-center justify-between">
                        <Step number="1" label="Account" active={stage === 1} complete={progress.account} />
                        <div className="mx-3 h-px flex-1 bg-gray-200" />
                        <Step number="2" label="Verify email" active={stage === 2} complete={progress.verification} />
                        <div className="mx-3 h-px flex-1 bg-gray-200" />
                        <Step number="3" label="Business" active={stage === 3} />
                    </div>

                    {stage === 1 && (
                        <form onSubmit={createAccount} className="space-y-5">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Create your Tiply account</h1>
                                <p className="mt-2 text-sm text-gray-600">Start with the owner’s email. Business setup takes only a few minutes.</p>
                            </div>
                            <label className="block text-sm font-semibold text-gray-700">Work email
                                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal focus:border-[#0d7c5f] focus:outline-none" />
                            </label>
                            <label className="block text-sm font-semibold text-gray-700">Password
                                <input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal focus:border-[#0d7c5f] focus:outline-none" />
                                <span className="mt-1 block text-xs font-normal text-gray-500">At least 8 characters.</span>
                            </label>
                            <button disabled={busy} className="w-full rounded-xl bg-[#0d7c5f] px-5 py-3 font-semibold text-white disabled:opacity-60">{busy ? "Creating account…" : "Continue"}</button>
                            <p className="text-center text-sm text-gray-600">Already registered? <Link to="/login" className="font-semibold text-[#0d7c5f]">Log in</Link></p>
                        </form>
                    )}

                    {stage === 2 && (
                        <div className="text-center">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e8f5f0] text-2xl">✉</div>
                            <h1 className="mt-5 text-2xl font-bold text-gray-900">Verify your email</h1>
                            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-gray-600">Click the link sent to <strong>{currentUser.email}</strong>. This page will continue automatically once verification is complete.</p>
                            <button type="button" onClick={refreshVerification} disabled={busy} className="mt-6 w-full rounded-xl bg-[#0d7c5f] px-5 py-3 font-semibold text-white disabled:opacity-60">{busy ? "Checking…" : "I verified my email"}</button>
                            <div className="mt-4 flex justify-center gap-5 text-sm">
                                <button type="button" onClick={resendVerification} disabled={busy} className="font-semibold text-[#0d7c5f]">Resend email</button>
                                <button type="button" onClick={() => signOut(auth)} className="font-semibold text-gray-500">Use another email</button>
                            </div>
                        </div>
                    )}

                    {stage === 3 && (
                        <form onSubmit={finishSignup} className="space-y-5">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Tell us about your business</h1>
                                <p className="mt-2 text-sm text-gray-600">Only the essentials for now. You can finish employee payout details from your dashboard.</p>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <label className="block text-sm font-semibold text-gray-700">Your first name<input name="repFirstName" value={business.repFirstName} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                                <label className="block text-sm font-semibold text-gray-700">Your last name<input name="repLastName" value={business.repLastName} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                            </div>
                            <label className="block text-sm font-semibold text-gray-700">Legal business name<input name="legalName" value={business.legalName} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                            <label className="block text-sm font-semibold text-gray-700">Name shown to customers<input name="displayName" value={business.displayName} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                            <label className="block text-sm font-semibold text-gray-700">Tip page URL
                                <div className="mt-2 flex overflow-hidden rounded-xl border border-gray-300 bg-white focus-within:border-[#0d7c5f]">
                                    <span className="flex items-center bg-gray-50 px-3 text-sm text-gray-500">tiply.ee/tip/</span>
                                    <input name="businessID" value={business.businessID} onChange={updateBusiness} minLength={3} maxLength={48} required className="min-w-0 flex-1 px-3 py-3 font-normal focus:outline-none" />
                                </div>
                                {availability?.checking && <span className="mt-1 block text-xs font-normal text-gray-500">Checking availability…</span>}
                                {!availability?.checking && availability?.available && <span className="mt-1 block text-xs font-normal text-[#0d7c5f]">This URL is available.</span>}
                                {!availability?.checking && availability && !availability.available && <span className="mt-1 block text-xs font-normal text-red-600">{availability.message || "This URL is already in use."}</span>}
                            </label>
                            <label className="block text-sm font-semibold text-gray-700">Business address<input name="address" value={business.address} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <label className="block text-sm font-semibold text-gray-700">Country<select name="countryCode" value={business.countryCode} onChange={updateBusiness} required className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-normal">{SUPPORTED_ONBOARDING_COUNTRIES.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}</select></label>
                                <label className="block text-sm font-semibold text-gray-700">Phone <span className="font-normal text-gray-400">(optional)</span><input name="repPhone" type="tel" value={business.repPhone} onChange={updateBusiness} className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal" /></label>
                            </div>
                            <label className="flex items-start gap-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                                <input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} required className="mt-1" />
                                <span>I agree to the <Link target="_blank" to="/terms" className="font-semibold text-[#0d7c5f] underline">Terms of Use</Link> and <Link target="_blank" to="/privacy" className="font-semibold text-[#0d7c5f] underline">Privacy Policy</Link>.</span>
                            </label>
                            <button disabled={busy || availability?.checking || availability?.available === false} className="w-full rounded-xl bg-[#0d7c5f] px-5 py-3 font-semibold text-white disabled:opacity-60">{busy ? "Creating business…" : "Create business and open dashboard"}</button>
                        </form>
                    )}

                    {(error || notice) && <p className={`mt-5 rounded-xl px-4 py-3 text-sm ${error ? "bg-red-50 text-red-700" : "bg-[#e8f5f0] text-[#075c47]"}`}>{error || notice}</p>}
                </div>
                <p className="mt-5 text-center text-xs text-gray-500">Need help? <a href="mailto:team@tiply.ee" className="underline">team@tiply.ee</a></p>
            </div>
        </div>
    );
}
